/**
 * Adds the open record to a list route's search: `/deposits?deposit=<id>`, so a record
 * opened in its detail sheet has a URL that survives a refresh and can be sent to someone.
 *
 * Wrapped around the filter parser rather than declared as a filter field. The filter
 * schema strips keys it does not know, and a field there would become a chip, go out
 * to the API as a filter, and be captured by saved views -- none of which an open sheet
 * should do. Validated on its own here, it is invisible to every filter projection.
 *
 * Anything but an ObjectId degrades to "nothing open" rather than to a sheet looking
 * up an id that cannot exist.
 */
export function withRecordParam<TInput, TOutput extends object, const TKey extends string>(
    parse: (search: TInput) => TOutput,
    key: TKey
) {
    return (search: TInput & { [K in TKey]?: unknown }): TOutput & { [K in TKey]?: string } => {
        const parsed = parse(search)
        const id = (search as Record<string, unknown> | undefined)?.[key]
        // Omitted rather than set to `undefined`, so a closed sheet leaves no key behind.
        return typeof id === 'string' && /^[a-f\d]{24}$/i.test(id) ? { ...parsed, [key]: id } : parsed
    }
}
